import React, { useState, useEffect } from "react";
import campusService from "../../services/campusService";
import universityService from "../../services/universityService";
import facultyService from "../../services/facultyService";
import departmentService from "../../services/departmentService";

const UserForm = ({ user, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    studentNumber: "",
    phoneNumber: "",
    gender: "MALE",
    campusId: "",
    universityId: "",
    facultyId: "",
    departmentId: "",
    educationLevel: 1,
  });

  const [dropdownData, setDropdownData] = useState({
    universities: [],
    campuses: [],
    faculties: [],
    departments: [],
  });

  const [dropdownLoading, setDropdownLoading] = useState({
    universities: false,
    campuses: false,
    faculties: false,
    departments: false,
  });

  const [errors, setErrors] = useState({});

  // Gender options
  const genderOptions = [
    { value: "MALE", label: "Male" },
    { value: "FEMALE", label: "Female" },
    { value: "OTHER", label: "Other" },
  ];

  // Education level options
  const educationLevelOptions = [
    { value: 1, label: "Prep Class" },
    { value: 2, label: "First Year" },
    { value: 3, label: "Second Year" },
    { value: 4, label: "Third Year" },
    { value: 5, label: "Fourth Year" },
    { value: 6, label: "Fifth Year" },
    { value: 7, label: "Sixth Year" },
    { value: 8, label: "Master Class" },
    { value: 9, label: "Doctorate" },
  ];

  // Load form data when user prop changes
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        password: "", // Don't populate password for editing
        studentNumber: user.studentNumber || "",
        phoneNumber: user.phoneNumber || "",
        gender: user.gender || "MALE",
        campusId: user.campusId || "",
        universityId: user.universityId || "",
        facultyId: user.facultyId || "",
        departmentId: user.departmentId || "",
        educationLevel: user.educationLevel || 1,
      });
    }
  }, [user]);

  // Load universities on component mount
  useEffect(() => {
    loadUniversities();
  }, []);

  // Load campuses when university changes
  useEffect(() => {
    if (formData.universityId) {
      loadCampuses(formData.universityId);
    } else {
      setDropdownData((prev) => ({ ...prev, campuses: [] }));
      setFormData((prev) => ({ ...prev, campusId: "" }));
    }
  }, [formData.universityId]);

  // Load faculties when campus changes
  useEffect(() => {
    if (formData.campusId) {
      loadFaculties(formData.campusId);
    } else {
      setDropdownData((prev) => ({ ...prev, faculties: [] }));
      setFormData((prev) => ({ ...prev, facultyId: "" }));
    }
  }, [formData.campusId]);

  // Load departments when faculty changes
  useEffect(() => {
    if (formData.facultyId) {
      loadDepartments(formData.facultyId);
    } else {
      setDropdownData((prev) => ({ ...prev, departments: [] }));
      setFormData((prev) => ({ ...prev, departmentId: "" }));
    }
  }, [formData.facultyId]);

  const loadUniversities = async () => {
    try {
      setDropdownLoading((prev) => ({ ...prev, universities: true }));
      const response = await universityService.getUniversities(0, 1000);
      if (response.success) {
        setDropdownData((prev) => ({
          ...prev,
          universities: response.data.data || [],
        }));
      }
    } catch (error) {
      console.error("Error loading universities:", error);
    } finally {
      setDropdownLoading((prev) => ({ ...prev, universities: false }));
    }
  };

  const loadCampuses = async (universityId) => {
    try {
      setDropdownLoading((prev) => ({ ...prev, campuses: true }));
      const response = await campusService.getCampuses(
        0,
        1000,
        "",
        universityId
      );
      if (response.success) {
        setDropdownData((prev) => ({
          ...prev,
          campuses: response.data.data || [],
        }));
      }
    } catch (error) {
      console.error("Error loading campuses:", error);
      setDropdownData((prev) => ({ ...prev, campuses: [] }));
    } finally {
      setDropdownLoading((prev) => ({ ...prev, campuses: false }));
    }
  };

  const loadFaculties = async (campusId) => {
    try {
      setDropdownLoading((prev) => ({ ...prev, faculties: true }));
      const response = await facultyService.getFaculties(0, 1000, "", campusId);
      if (response.success) {
        setDropdownData((prev) => ({
          ...prev,
          faculties: response.data.data || [],
        }));
      }
    } catch (error) {
      console.error("Error loading faculties:", error);
      setDropdownData((prev) => ({ ...prev, faculties: [] }));
    } finally {
      setDropdownLoading((prev) => ({ ...prev, faculties: false }));
    }
  };

  const loadDepartments = async (facultyId) => {
    try {
      setDropdownLoading((prev) => ({ ...prev, departments: true }));
      const response = await departmentService.getDepartments(
        0,
        1000,
        "",
        "",
        facultyId
      );
      if (response.success) {
        setDropdownData((prev) => ({
          ...prev,
          departments: response.data.data || [],
        }));
      }
    } catch (error) {
      console.error("Error loading departments:", error);
      setDropdownData((prev) => ({ ...prev, departments: [] }));
    } finally {
      setDropdownLoading((prev) => ({ ...prev, departments: false }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue = value;
    if (type === "checkbox") {
      newValue = checked;
    }

    setFormData((prev) => {
      const updated = { ...prev, [name]: newValue };

      // Reset dependent dropdowns when parent changes
      if (name === "universityId") {
        updated.campusId = "";
        updated.facultyId = "";
        updated.departmentId = "";
      } else if (name === "campusId") {
        updated.facultyId = "";
        updated.departmentId = "";
      } else if (name === "facultyId") {
        updated.departmentId = "";
      }

      return updated;
    });

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.firstName?.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    // Password is required only for new users
    if (!user && !formData.password?.trim()) {
      newErrors.password = "Password is required";
    } else if (!user && formData.password && formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!formData.studentNumber?.trim()) {
      newErrors.studentNumber = "Student number is required";
    }

    if (!formData.universityId) {
      newErrors.universityId = "University is required";
    }

    if (!formData.campusId) {
      newErrors.campusId = "Campus is required";
    }

    // Phone number validation (optional but if provided, should be valid)
    if (
      formData.phoneNumber &&
      !/^\+?[\d\s\-()+]+$/.test(formData.phoneNumber)
    ) {
      newErrors.phoneNumber = "Invalid phone number format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Prepare submit data
      const submitData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        studentNumber: formData.studentNumber.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        gender: formData.gender,
        campusId: formData.campusId,
        universityId: formData.universityId,
        facultyId: formData.facultyId || null,
        departmentId: formData.departmentId || null,
        educationLevel: formData.educationLevel,
      };

      // Add password only for new users
      if (!user && formData.password) {
        submitData.password = formData.password;
      }

      onSubmit(submitData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="user-form">
      <div className="row g-4">
        {/* Personal Information Section */}
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-user"></i>
              <h6>Personal Information</h6>
            </div>

            <div className="row g-3">
              {/* First Name */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  First Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  className={`form-control ${
                    errors.firstName ? "is-invalid" : ""
                  }`}
                  placeholder="Enter first name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.firstName && (
                  <div className="invalid-feedback">{errors.firstName}</div>
                )}
              </div>

              {/* Last Name */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  Last Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  className={`form-control ${
                    errors.lastName ? "is-invalid" : ""
                  }`}
                  placeholder="Enter last name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.lastName && (
                  <div className="invalid-feedback">{errors.lastName}</div>
                )}
              </div>

              {/* Email */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={loading || !!user} // Disable email editing for existing users
                />
                {errors.email && (
                  <div className="invalid-feedback">{errors.email}</div>
                )}
                {user && (
                  <small className="text-muted">
                    Email cannot be changed for existing users
                  </small>
                )}
              </div>

              {/* Password - only for new users */}
              {!user && (
                <div className="col-md-6">
                  <label className="form-label fw-bold">
                    Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    className={`form-control ${
                      errors.password ? "is-invalid" : ""
                    }`}
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  {errors.password && (
                    <div className="invalid-feedback">{errors.password}</div>
                  )}
                  <small className="text-muted">Minimum 6 characters</small>
                </div>
              )}

              {/* Student Number */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  Student Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="studentNumber"
                  className={`form-control ${
                    errors.studentNumber ? "is-invalid" : ""
                  }`}
                  placeholder="Enter student number"
                  value={formData.studentNumber}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.studentNumber && (
                  <div className="invalid-feedback">{errors.studentNumber}</div>
                )}
              </div>

              {/* Phone Number */}
              <div className="col-md-6">
                <label className="form-label fw-bold">Phone Number</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  className={`form-control ${
                    errors.phoneNumber ? "is-invalid" : ""
                  }`}
                  placeholder="+1 (555) 123-4567"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.phoneNumber && (
                  <div className="invalid-feedback">{errors.phoneNumber}</div>
                )}
              </div>

              {/* Gender */}
              <div className="col-md-6">
                <label className="form-label fw-bold">Gender</label>
                <select
                  name="gender"
                  className="form-select"
                  value={formData.gender}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  {genderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Education Level */}
              <div className="col-md-6">
                <label className="form-label fw-bold">Education Level</label>
                <select
                  name="educationLevel"
                  className="form-select"
                  value={formData.educationLevel}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  {educationLevelOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Academic Information Section */}
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-graduation-cap"></i>
              <h6>Academic Information</h6>
            </div>

            <div className="row g-3">
              {/* University */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  University <span className="text-danger">*</span>
                </label>
                <select
                  name="universityId"
                  className={`form-select ${
                    errors.universityId ? "is-invalid" : ""
                  }`}
                  value={formData.universityId}
                  onChange={handleInputChange}
                  disabled={loading || dropdownLoading.universities}
                >
                  <option value="">Select University</option>
                  {dropdownData.universities.map((university) => (
                    <option key={university.id} value={university.id}>
                      {university.nameEn}
                    </option>
                  ))}
                </select>
                {errors.universityId && (
                  <div className="invalid-feedback">{errors.universityId}</div>
                )}
                {dropdownLoading.universities && (
                  <small className="text-muted">Loading universities...</small>
                )}
              </div>

              {/* Campus */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  Campus <span className="text-danger">*</span>
                </label>
                <select
                  name="campusId"
                  className={`form-select ${
                    errors.campusId ? "is-invalid" : ""
                  }`}
                  value={formData.campusId}
                  onChange={handleInputChange}
                  disabled={
                    loading ||
                    dropdownLoading.campuses ||
                    !formData.universityId
                  }
                >
                  <option value="">Select Campus</option>
                  {dropdownData.campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.nameEn} - {campus.city}, {campus.country}
                    </option>
                  ))}
                </select>
                {errors.campusId && (
                  <div className="invalid-feedback">{errors.campusId}</div>
                )}
                {dropdownLoading.campuses && (
                  <small className="text-muted">Loading campuses...</small>
                )}
                {!formData.universityId && (
                  <small className="text-muted">
                    Please select a university first
                  </small>
                )}
              </div>

              {/* Faculty */}
              <div className="col-md-6">
                <label className="form-label fw-bold">Faculty</label>
                <select
                  name="facultyId"
                  className="form-select"
                  value={formData.facultyId}
                  onChange={handleInputChange}
                  disabled={
                    loading || dropdownLoading.faculties || !formData.campusId
                  }
                >
                  <option value="">Select Faculty</option>
                  {dropdownData.faculties.map((faculty) => (
                    <option key={faculty.id} value={faculty.id}>
                      {faculty.nameEn}
                    </option>
                  ))}
                </select>
                {dropdownLoading.faculties && (
                  <small className="text-muted">Loading faculties...</small>
                )}
                {!formData.campusId && (
                  <small className="text-muted">
                    Please select a campus first
                  </small>
                )}
              </div>

              {/* Department */}
              <div className="col-md-6">
                <label className="form-label fw-bold">Department</label>
                <select
                  name="departmentId"
                  className="form-select"
                  value={formData.departmentId}
                  onChange={handleInputChange}
                  disabled={
                    loading ||
                    dropdownLoading.departments ||
                    !formData.facultyId
                  }
                >
                  <option value="">Select Department</option>
                  {dropdownData.departments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.nameEn}
                    </option>
                  ))}
                </select>
                {dropdownLoading.departments && (
                  <small className="text-muted">Loading departments...</small>
                )}
                {!formData.facultyId && (
                  <small className="text-muted">
                    Please select a faculty first
                  </small>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={loading}
        >
          <i className="fas fa-times me-2"></i>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              {user ? "Updating..." : "Creating..."}
            </>
          ) : (
            <>
              <i className={`fas ${user ? "fa-save" : "fa-plus"} me-2`}></i>
              {user ? "Update User" : "Create User"}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default UserForm;
